// ─────────────────────────────────────────────────────────────
// Measurement Protocol — INDEX (LIST) PAGE
//
// Moved out of the original monolithic MPCreationIndex.js. UI is
// unchanged. Behavior fixes made here:
//
// 1. "Initiate New MP" is now the separate <MPCreatePage /> component
//    (its own file/page) instead of inline JSX + inline state.
//
// 2. `onSaved` is now actually wired down into <NestedMPTable /> (it
//    was previously accepted by NestedMPTable/MPExpandedRow/every tab
//    but never passed in from here, so a tab's Save never refreshed
//    anything). `handleMPSaved` below re-fetches just the nested MP
//    rows for the currently expanded Test Overview row plus the
//    header counts — it does NOT touch `expandedRowKey`,
//    `expandedMPID`, or any tab's `activeTab`, so the row you're
//    working in stays open and on the same tab while its data (and
//    the surrounding list/counters) silently refreshes.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  toArray,
  BRAND,
  Badge,
  ExpandToggle,
  ColSearch,
  TH,
  TD,
  Empty,
  Toast,
} from "./shared/mpShared";
import MPCreatePage from "./MPCreatePage";
import NestedMPTable from "./NestedMPTable";
import DashboardLayout from "../../Layout/DashboardLayout";
import Pagination from "../../Common/Tables/Pagination";
import {
  Button,
  companyID,
  ConfirmDialog,
  employeeID,
  hasPrivilege,
  Icon,
  PRIVILEGES,
} from "../../Common/UIComponents";
import { useGlobalLoader } from "../../Common/GlobalLoader";
import {
  MP_CreateDuplicateMP,
  MP_DeleteMPDetails,
  MP_GetGetMeasurementListbyID,
  MP_GetMPList,
} from "../../API_Service/MeasurementProtocol_Slice";
import { fetchDateFormatConfig } from "../../API_Service/Settings_Slice";

const formatDate = (dateString, format = "dd-MM-yyyy") => {
  if (!dateString) return "";
  
  const str = dateString.slice(0, 10);
  let dd, MM, yyyy;
  
  if (str.includes("-") && str.split("-")[0].length === 2) {
    [dd, MM, yyyy] = str.split("-");
  } else if (str.includes("-") && str.split("-")[0].length === 4) {
    [yyyy, MM, dd] = str.split("-");
  } else {
    return dateString;
  }
  
  const tempDate = new Date(`${yyyy}-${MM}-${dd}T00:00:00`);
  if (isNaN(tempDate.getTime())) return dateString;
  const MMM = tempDate.toLocaleString("en-US", { month: "short" });
  
  return format.replace("MMM", MMM).replace("MM", MM).replace("dd", dd).replace("yyyy", yyyy);
};

export default function MPListIndex() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [testOverview, setTestOverview] = useState([]);
  const [mpList, setMPList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [nestedMPRows, setNestedMPRows] = useState([]);
  const [expandedMPID, setExpandedMPID] = useState(null);

  const rowKey = (testTypeID, facilityId) => `${testTypeID}::${facilityId}`;
  const expandedTestTypeID = expandedRowKey
    ? expandedRowKey.split("::")[0]
    : null;
  const expandedFacilityID = expandedRowKey
    ? expandedRowKey.split("::")[1]
    : null;

  const [isInitiateOpen, setIsInitiateOpen] = useState(false);
  const [totalMP, setTotalMP] = useState(0);
  const [inprogressMP, setInprogressMP] = useState(0);

  // Pagination for the Test Overview list — uses the shared <Pagination />
  // component so it matches every other list page in the app.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Bumped every time the user hits Refresh. Passed down to the nested MP
  // table so the currently-open MP editor (its tabs) re-mounts and reloads
  // its own data — i.e. the *active* MP row refreshes, not just the list.
  const [refreshKey, setRefreshKey] = useState(0);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, mp: null });
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    mp: null,
  });

  const [ovSrch, setOvSrch] = useState({
    testType: "",
    testName: "",
    facility: "",
    startDate: "",
    endDate: "",
    status: "",
  });
  const [mpSrch, setMpSrch] = useState({
    mpNo: "",
    testName: "",
    facility: "",
    startDate: "",
    endDate: "",
    dutRef: "",
    instrNo: "",
    status: "",
  });

  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [userDateFormat, setUserDateFormat] = useState("dd-MM-yyyy");

  useEffect(() => {
    dispatch(fetchDateFormatConfig({ companyID }))
      .unwrap()
      .then((res) => {
        const data = res?.data ?? res;
        const config = Array.isArray(data) ? data[0] : data;
        if (config?.DateFormat) setUserDateFormat(config.DateFormat);
      })
      .catch(console.error);
  }, [dispatch]);

  const showToast = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast({ show: false, msg: "", type: "success" }), 2500);
  };

  const fetchMPList = useCallback(async () => {
    try {
      const res = await dispatch(
        MP_GetMPList({ companyID, testTypeID: 0, facilityID: 0 }),
      );
      const data = toArray(res?.payload?.data);
      setTestOverview(
        data.map((t) => ({
          MPID: t.MP_Id,
          TestTypeID: t.Equipment_Test_Type_ID,
          TestTypeName: t.Equipment_Test_Type_nm,
          TestName: t.TestNames ?? t.TestName ?? t.Test_Name ?? "",
          FacilityId: t.Facility_Id,
          FacilityName: t.Facility_Name,
          StartDate: t.StartDate,
          EndDate: t.EndDate,
          Status: t.Status,
        })),
      );
      setTotalMP(data[0]?.TotalMP);
      setInprogressMP(data[0]?.Inprogress);
    } catch (err) {
      console.error("Error fetching MP list:", err);
      showToast(
        "Couldn't load the Test Overview list. Please refresh.",
        "error",
      );
    }
  }, [dispatch]);

  // Fetch the nested MP rows for whichever Test Overview row is
  // currently expanded. Extracted into its own callback (rather than
  // living only inside a useEffect) so it can be re-run on demand —
  // both when the expanded row changes AND after a tab saves data for
  // an MP inside it — without resetting any expand/tab state.
  const fetchNestedRows = useCallback(async () => {
    if (expandedTestTypeID === null || expandedFacilityID === null) {
      setNestedMPRows([]);
      return;
    }
    try {
      const res = await dispatch(
        MP_GetGetMeasurementListbyID({
          companyID,
          testTypeID: expandedTestTypeID,
          facilityID: expandedFacilityID,
        }),
      );
      const data = toArray(res?.payload?.data);

      const formattedData = data.map((m) => ({
        MPID: m.MP_Id,
        MPNo: m.MP_Code,
        TestName: m.TestNames,
        FacilityName: m.Facility_Name,
        StartDate: m.StartDate,
        EndDate: m.EndDate,
        DUTRefNo: m.DUT_Ref_No,
        InstrumentNo: m.Instrument_No,
        Status: m.Status,
        IsCanceled: m.Is_Canceled,
        ResultList: m.ResultList ?? [],
        ProjectName: m.Project_Name ?? "",
      }));

      setNestedMPRows(formattedData);
    } catch (err) {
      console.error("Error fetching nested MP rows:", err);
      setNestedMPRows([]);
      showToast("Couldn't load Measurement Protocols for this row.", "error");
    }
  }, [dispatch, expandedTestTypeID, expandedFacilityID]);

  useEffect(() => {
    fetchMPList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNestedRows();
  }, [fetchNestedRows]);

  // Called by any tab (via MPExpandedRow → NestedMPTable) once it has
  // successfully saved. Refreshes the nested MP rows for the row
  // that's open, plus the header Total/Inprogress counters — but
  // deliberately does NOT touch expandedRowKey / expandedMPID, so the
  // row and tab the user is currently working in stays exactly as-is.
  const handleMPSaved = useCallback(() => {
    fetchNestedRows();
    fetchMPList();
  }, [fetchNestedRows, fetchMPList]);

  const handleToggleTest = (TestTypeID, facilityId) => {
    const key = rowKey(TestTypeID, facilityId);
    setExpandedRowKey((prev) => (prev === key ? null : key));
    setExpandedMPID(null);
    setMpSrch({
      mpNo: "",
      testName: "",
      facility: "",
      startDate: "",
      endDate: "",
      dutRef: "",
      instrNo: "",
      status: "",
    });
  };

  const handleToggleMP = (mpID) => {
    setExpandedMPID((prev) => (prev === mpID ? null : mpID));
  };

  // Called by MPCreatePage once a new MP has actually been initiated.
  const handleMPCreated = async ({ testTypeID, facilityID }) => {
    await fetchMPList();
    setExpandedRowKey(rowKey(testTypeID, facilityID));
  };

  // Opens the confirm dialog for the MP the user clicked delete on.
  // `mp` is the row object from nestedMPRows (so we have MPNo for the message).
  const handleDelete = useCallback((mp) => {
    setDeleteBlocked(false);
    setDeleteDialog({ open: true, mp });
  }, []);

  // Fires when the user confirms deletion in the dialog.
  const confirmDelete = async () => {
    const { mp } = deleteDialog;
    const mpID = mp?.MPID;
    if (!mpID) return;
    try {
      const payload = {
        id: mpID,
        code: "MP",
        modifiedBy: employeeID,
      };
      const res = await dispatch(MP_DeleteMPDetails(payload));
      if (res?.payload?.data > 0) {
        setNestedMPRows((prev) => prev.filter((m) => m.MPID !== mpID));
        setMPList((prev) => prev.filter((m) => m.MPID !== mpID));
        if (expandedMPID === mpID) setExpandedMPID(null);
        fetchMPList(); // keep Total/Inprogress counters in sync
        showToast("MP deleted.");
        setDeleteDialog({ open: false, mp: null });
      } else {
        showToast("Couldn't delete the MP. Please try again.", "error");
      }
    } catch (err) {
      console.error("Error deleting MP:", err);
      showToast("Couldn't delete the MP. Please try again.", "error");
    }
  };

  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ open: false, mp: null });
    setDeleteBlocked(false);
  }, []);

  const handleDuplicate = useCallback((mp) => {
    setDuplicateDialog({ open: true, mp });
  }, []);

  const confirmDuplicate = async () => {
    const { mp } = duplicateDialog;
    const mpID = mp?.MPID;
    if (!mpID) return;
    try {
      const payload = {
        mpID,
        modifiedBy: employeeID,
        companyID,
      };
      const res = await dispatch(MP_CreateDuplicateMP(payload));
      if (
        res?.payload?.data > 0 ||
        res?.payload?.status === 200 ||
        res?.meta?.requestStatus === "fulfilled"
      ) {
        await Promise.all([fetchMPList(), fetchNestedRows()]);
        showToast("MP duplicated successfully.");
        setDuplicateDialog({ open: false, mp: null });
      } else {
        showToast("Couldn't duplicate the MP. Please try again.", "error");
      }
    } catch (err) {
      console.error("Error duplicating MP:", err);
      showToast("Couldn't duplicate the MP. Please try again.", "error");
    }
  };

  const handleCancelDuplicate = useCallback(() => {
    setDuplicateDialog({ open: false, mp: null });
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    showLoader();
    try {
      await Promise.all([fetchMPList(), fetchNestedRows()]);
      // Re-mount the open MP editor so the active MP's tab data reloads too.
      setRefreshKey((k) => k + 1);
      showToast("Refreshed.");
    } catch (err) {
      console.error("Error refreshing page data:", err);
      showToast("Couldn't refresh. Please try again.", "error");
    } finally {
      setTimeout(() => setLoading(false), 400);
      hideLoader();
    }
  };

  const filteredOv = useMemo(
    () =>
      testOverview?.filter((r) =>
        Object.entries(ovSrch).every(([k, v]) => {
          if (!v) return true;
          const m = {
            testType: r.TestTypeName,
            testName: r.TestName,
            facility: r.FacilityName,
            startDate: r.StartDate,
            endDate: r.EndDate,
            status: r.Status,
            TestTypeID: r.TestTypeID,
            FacilityId: r.FacilityId,
          };
          return (m[k] ?? "")
            .toString()
            .toLowerCase()
            .includes(v.toLowerCase());
        }),
      ),
    [testOverview, ovSrch],
  );

  // Pagination derived values for the Test Overview list.
  const totalPages = Math.max(
    1,
    Math.ceil((filteredOv?.length || 0) / pageSize),
  );
  const pagedOv = useMemo(
    () =>
      (filteredOv || []).slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredOv, currentPage, pageSize],
  );

  // Keep the current page valid if filtering/refresh shrinks the list.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // Changing a column search should always jump back to the first page.
  const setOv = (k, v) => {
    setCurrentPage(1);
    setOvSrch((s) => ({ ...s, [k]: v }));
  };
  const setMp = (k, v) => setMpSrch((s) => ({ ...s, [k]: v }));

  return (
    <DashboardLayout>
      <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

      <Toast msg={toast.msg} show={toast.show} type={toast.type} />

      <div
        className="min-h-screen w-full max-w-full overflow-x-hidden"
        style={{ background: "#f0f4f4" }}
      >
        <div className="px-4 sm:px-6 pt-3 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <nav className="flex items-center gap-2 text-xs text-[#6e797a]">
              <span>Lab Management</span>
              <span className="material-symbols-outlined text-[13px]">
                chevron_right
              </span>
              <span className="font-semibold" style={{ color: BRAND }}>
                Measurement Protocol
              </span>
            </nav>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-xl border border-[#e5e9e9] bg-white hover:bg-[#f0f4f4] transition-all text-[#3e494a]"
            >
              <span className="material-symbols-outlined text-[14px]">
                west
              </span>
              Back
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1
                className="text-xl font-black text-[#181c1d] tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                MP Creation
              </h1>
              <p className="text-[#6e797a] text-sm mt-0.5">
                Expand a Test Overview row to view and manage its Measurement
                Protocols
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
           
              <div className="flex items-center justify-center gap-1.5 bg-white rounded-2xl px-3 py-1.5 border border-[#e5e9e9] shadow-sm min-w-[108px]">
                <p className="text-xl font-black" style={{ color: BRAND }}>
                  {totalMP}
                </p>
                <p className="text-[10px] font-bold text-[#6e797a] uppercase tracking-wide whitespace-nowrap">
                  Total MPs
                </p>
              </div>
              <div className="flex items-center justify-center gap-1.5 bg-white rounded-2xl px-3 py-1.5 border border-[#e5e9e9] shadow-sm min-w-[108px]">
                <p className="text-xl font-black text-emerald-600">
                  {inprogressMP}
                </p>
                <p className="text-[10px] font-bold text-[#6e797a] uppercase tracking-wide whitespace-nowrap">
                  Inprogress
                </p>
              </div>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-3 py-2 rounded-2xl border border-[#e5e9e9] bg-white text-[#3e494a] font-semibold text-sm hover:bg-[#f0f4f4] transition-all shadow-sm"
              >
                <span
                  className={`material-symbols-outlined text-[16px]${loading ? " animate-spin" : ""}`}
                >
                  refresh
                </span>
                Refresh
              </button>
              {hasPrivilege(PRIVILEGES.MP_CREATE) && (
                <Button
                  variant="primary"
                  onClick={() => setIsInitiateOpen((v) => !v)}
                >
                  <Icon
                    name={isInitiateOpen ? "close" : "add"}
                    className="text-[18px]"
                  />
                  {isInitiateOpen ? "Cancel" : "MP New"}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 pb-4 space-y-3 w-full max-w-full min-w-0">
          <MPCreatePage
            open={isInitiateOpen}
            onClose={() => setIsInitiateOpen(false)}
            onCreated={handleMPCreated}
            notify={showToast}
          />

          <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm overflow-hidden w-full max-w-full min-w-0">
            <div className="px-4 py-2 border-b border-[#e5e9e9] flex items-center gap-3 bg-[#f7fafa]">
              <span
                className="material-symbols-outlined text-[16px]"
                style={{ color: BRAND }}
              >
                biotech
              </span>
              <h2 className="text-sm font-extrabold text-[#181c1d]">
                Test Overview
              </h2>
              <span className="text-[11px] text-[#9eabac] font-medium">
                {filteredOv?.length} record{filteredOv?.length !== 1 ? "s" : ""}
              </span>
            </div>

            {expandedRowKey === null &&
              !hasPrivilege(PRIVILEGES.EMAIL_EDIT) && (
                <div className="flex items-center gap-3 px-4 py-1.5 bg-teal-50 border-b border-teal-100">
                  <span className="material-symbols-outlined text-[16px] text-[#08757C]">
                    info
                  </span>
                  <p className="text-[12px] text-[#08757C] font-medium">
                    Click the <span className="font-bold">+</span> on any row to
                    expand it and view its Measurement Protocols nested directly
                    underneath.
                  </p>
                </div>
              )}

            <div className="overflow-x-auto w-full max-w-full">
              <table className="w-full min-w-[860px] border-collapse text-[11.5px]">
                <thead>
                  <tr>
                    <TH w={40}></TH>
                    <TH>Test Type</TH>
                    <TH>Facility</TH>
                    <TH>Start Date</TH>
                    <TH>End Date</TH>
                    <TH>Status</TH>
                    <TH>Options</TH>
                  </tr>
                  <tr className="bg-white border-b border-[#ebeeee]">
                    <td className="px-2 py-1" />
                    {[
                      ["testType"],
                      ["facility"],
                      ["startDate"],
                      ["endDate"],
                      ["status"],
                    ]?.map(([k]) => (
                      <td key={k} className="px-2 py-1">
                        <ColSearch
                          value={ovSrch[k]}
                          onChange={(v) => setOv(k, v)}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1" />
                    <td className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {filteredOv?.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <Empty icon="science" msg="No test records" />
                      </td>
                    </tr>
                  ) : (
                    pagedOv?.map((row, idx) => {
                      const isExpanded =
                        rowKey(row.TestTypeID, row.FacilityId) ===
                        expandedRowKey;
                      return (
                        <Fragment
                          key={`ov-frag-${row.TestTypeID}-${row.FacilityId}`}
                        >
                          <tr
                            onClick={() =>
                              handleToggleTest(row.TestTypeID, row.FacilityId)
                            }
                            className={`transition-all cursor-pointer
                              ${
                                isExpanded
                                  ? "bg-[#e6f4f4] border-l-4 border-l-[#08757C]"
                                  : idx % 2 === 0
                                    ? "bg-white hover:bg-[#f7fafa]"
                                    : "bg-[#fafcfc] hover:bg-[#f7fafa]"
                              }`}
                          >
                            <td className="px-3 py-1.5 border-b border-[#f1f4f4] w-10">
                              <ExpandToggle
                                open={isExpanded}
                                onClick={() =>
                                  handleToggleTest(
                                    row.TestTypeID,
                                    row.FacilityId,
                                  )
                                }
                              />
                            </td>
                            <TD>
                              <span
                                className="font-semibold"
                                style={{ color: BRAND }}
                              >
                                {row.TestTypeName ?? "—"}
                              </span>
                            </TD>
                            <TD
                              style={{
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.FacilityName ?? "—"}
                            </TD>
                            <TD>{row.StartDate ? formatDate(row.StartDate, userDateFormat) : "—"}</TD>
                            <TD>{row.EndDate ? formatDate(row.EndDate, userDateFormat) : "—"}</TD>
                            <TD>
                              <Badge status={row.Status} />
                            </TD>
                            <TD>
                              <span
                                className={`text-[10px] font-bold ${isExpanded ? "text-[#08757C]" : "text-[#9eabac]"}`}
                              >
                                {isExpanded ? "▲ Collapse" : "Click to expand"}
                              </span>
                            </TD>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td
                                colSpan={8}
                                className="p-0 border-0 bg-[#f0f4f4]"
                              >
                                <div
                                  className="p-2"
                                  style={{ animation: "fadeUp .2s ease" }}
                                >
                                  <NestedMPTable
                                    testRow={row}
                                    mpRows={nestedMPRows.map(m => ({
                                      ...m,
                                      StartDate: m.StartDate ? formatDate(m.StartDate, userDateFormat) : "",
                                      EndDate: m.EndDate ? formatDate(m.EndDate, userDateFormat) : ""
                                    }))}
                                    expandedMPID={expandedMPID}
                                    onToggleMP={handleToggleMP}
                                    mpSrch={mpSrch}
                                    onSearchChange={setMp}
                                    onSaved={handleMPSaved}
                                    onDelete={handleDelete}
                                    onDuplicate={handleDuplicate}
                                    refreshKey={refreshKey}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {filteredOv?.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredOv.length}
                itemsPerPage={pageSize}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        title="Delete Measurement Protocol"
        message={`Are you sure you want to delete "${deleteDialog.mp?.MPNo || "this MP"}"? This action cannot be undone.`}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={confirmDelete}
        onCancel={handleCancelDelete}
      />
      <ConfirmDialog
        isOpen={duplicateDialog.open}
        variant="info"
        title="Duplicate Measurement Protocol"
        message={`Are you sure you want to duplicate "${duplicateDialog.mp?.MPNo || "this MP"}"?`}
        cancelLabel="Cancel"
        onConfirm={confirmDuplicate}
        onCancel={handleCancelDuplicate}
      />
    </DashboardLayout>
  );
}
